import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export default function DPDPCompliance() {
  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="DPDP Act 2023 Compliance — Redcubical Systems"
        description="How Redcubical Systems complies with India's Digital Personal Data Protection Act 2023. Data storage, user rights, and compliance framework."
        canonical="/dpdp-compliance"
      />

      <section style={{
        padding: '80px 32px 120px',
        background: 'var(--black)',
        minHeight: '100vh',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ marginBottom: '56px' }}>
            <span className="section-label" style={{ marginBottom: '16px' }}>
              Legal · Compliance
            </span>
            <h1 style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              fontSize: 'clamp(32px, 5vw, 60px)',
              fontWeight: '900', color: '#fff',
              textTransform: 'uppercase',
              lineHeight: 1.05, marginBottom: '16px',
            }}>
              DPDP ACT 2023<br />
              <span className="gradient-text">COMPLIANCE</span>
            </h1>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.35)',
              lineHeight: '1.6',
            }}>
              Digital Personal Data Protection Act 2023 ·
              How Redcubical Systems protects your data
            </p>
          </div>

          {/* Compliance status banner */}
          <div style={{
            padding: '28px 32px',
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.2)',
            borderRadius: '16px',
            marginBottom: '48px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}>
            <div style={{
              width: '48px', height: '48px',
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: '12px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px', flexShrink: 0,
            }}>
              ✅
            </div>
            <div>
              <p style={{
                fontSize: '15px', fontWeight: '700',
                color: '#22C55E', marginBottom: '4px',
              }}>
                DPDP Act 2023 Compliant
              </p>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.45)',
              }}>
                All Redcubical Systems products are built in
                compliance with India's Digital Personal Data
                Protection Act 2023
              </p>
            </div>
          </div>

          {/* Quick compliance facts */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            marginBottom: '56px',
          }} className="dpdp-facts-grid">
            {[
              { icon: '🇮🇳', label: 'Data Residency', value: 'India only (Mumbai servers)' },
              { icon: '🔒', label: 'Encryption', value: 'AES-256 at rest · TLS 1.3 in transit' },
              { icon: '📋', label: 'Consent', value: 'Explicit opt-in for all data collection' },
              { icon: '🗑️', label: 'Right to Erasure', value: 'Honoured within 30 days of request' },
            ].map((fact) => (
              <div key={fact.label} style={{
                padding: '20px 24px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.12)',
                borderRadius: '14px',
              }}>
                <span style={{ fontSize: '24px', display: 'block', marginBottom: '10px' }}>
                  {fact.icon}
                </span>
                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  textTransform: 'uppercase', letterSpacing: '1px',
                  marginBottom: '4px',
                }}>
                  {fact.label}
                </p>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.65)',
                  fontWeight: '500',
                }}>
                  {fact.value}
                </p>
              </div>
            ))}
          </div>

          {/* Main content */}
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '40px',
          }}>
            {[
              {
                title: '1. About the DPDP Act 2023',
                content: `The Digital Personal Data Protection Act 2023 (DPDP Act) is India's landmark data protection legislation enacted by the Parliament of India. It governs the processing of digital personal data in India and grants individuals significant rights over their personal data.

Redcubical Systems Private Limited is fully committed to compliance with the DPDP Act 2023 across all our products — HealHub, Suraksha, Vedant, HealHub Nexus, and our website.`,
              },
              {
                title: '2. Our Role Under the DPDP Act',
                content: `Redcubical Systems operates in two capacities under the DPDP Act:

As a Data Fiduciary: When we collect and process data from visitors to our website, users of our direct products (Suraksha), and individuals who contact us — we determine the purpose and means of processing and act as the Data Fiduciary.

As a Data Processor: When clinics use HealHub to manage their patients' data — the clinic is the Data Fiduciary and Redcubical Systems acts as a Data Processor on their behalf, processing data only as instructed by the clinic.`,
              },
              {
                title: '3. Lawful Basis for Processing',
                content: `We process personal data only on the following lawful bases as defined under the DPDP Act 2023:

Consent: We obtain free, specific, informed, unconditional, and unambiguous consent before processing personal data. Users can withdraw consent at any time.

Legitimate Uses: We process data where necessary for the performance of our contract with you (providing software services you have subscribed to).

Legal Obligation: Where required to comply with any law or court order in India.

We do not process personal data for purposes beyond those for which consent was obtained.`,
              },
              {
                title: '4. Data Principal Rights',
                content: `Under the DPDP Act 2023, you as a Data Principal have the following rights which we fully honour:

Right to Information: You have the right to know what personal data we process about you, the purpose of processing, and the identity of third parties with whom data is shared.

Right to Correction and Erasure: You have the right to correct inaccurate data and to have your personal data erased when it is no longer necessary for the purpose for which it was collected.

Right to Grievance Redressal: You have the right to file a grievance with our Grievance Officer and receive a response within 30 days.

Right to Nominate: You may nominate another individual to exercise your data rights in the event of your death or incapacity.

To exercise any of these rights, contact: a.uniyal@redcubicalsystems.com`,
              },
              {
                title: '5. Consent Management',
                content: `We implement consent management as follows:

Website: Cookie consent is obtained on first visit. Non-essential cookies are only activated after consent is given.

Suraksha App: During onboarding, users are presented with individual permission toggles for each data type (calls, contacts, SMS, location, overlay). Each permission has a clear explanation of why it is needed. Users can deny any permission and still use the app with reduced functionality.

HealHub: Clinics agree to our Data Processing Agreement which governs how patient data is handled. Clinics are responsible for obtaining patient consent for data collection.

Contact Forms: All forms include a clear statement that data submitted is used only to respond to the enquiry.`,
              },
              {
                title: '6. Data Localisation',
                content: `In compliance with the DPDP Act's data localisation requirements:

All personal data of Indian users is stored exclusively on servers located within India.

We use Firebase's Mumbai (asia-south1) region for all database and authentication services.

No personal data of Indian users is transferred to servers outside India without explicit consent and appropriate safeguards.

Our third-party AI processing (OpenAI for Vedant) operates under contractual data processing agreements that include appropriate safeguards for any data processing.`,
              },
              {
                title: '7. Data Protection by Design',
                content: `We implement data protection by design and by default across all our products:

Minimisation: We collect only the minimum data necessary for the specified purpose. We do not collect data "just in case."

Purpose Limitation: Data collected for one purpose is not used for another purpose without fresh consent.

Storage Limitation: Data is retained only for as long as necessary. We have defined retention periods for each data category.

Security by Default: Our most privacy-protective settings are the default. Users must actively opt-in to share additional data.`,
              },
              {
                title: '8. Security Measures',
                content: `We implement the following technical and organisational measures to protect personal data:

Technical Measures:
— AES-256 encryption for all data stored at rest
— TLS 1.3 for all data transmitted over networks
— Role-based access control — minimum necessary access principle
— Multi-factor authentication for administrative access
— Regular automated security scanning
— No sensitive data in application logs

Organisational Measures:
— Privacy impact assessments for new features
— Data breach response procedure with 72-hour notification commitment
— Regular staff awareness on data protection obligations
— Vendor due diligence for all third-party data processors`,
              },
              {
                title: '9. Data Breach Notification',
                content: `In the event of a personal data breach:

We will notify affected Data Principals within 72 hours of becoming aware of a breach that is likely to result in high risk to their rights and freedoms.

Notification will include the nature of the breach, categories and approximate number of individuals affected, likely consequences, and measures taken or proposed to address the breach.

We will also notify the Data Protection Board of India as required under the DPDP Act 2023.

To report a suspected security incident: a.uniyal@redcubicalsystems.com`,
              },
              {
                title: '10. Children\'s Data',
                content: `The DPDP Act 2023 provides special protection for children's data (individuals under 18).

We do not knowingly collect personal data from children under 18 without verifiable parental consent.

Our products are not directed at children under 18.

If we become aware that we have collected personal data from a child without appropriate consent, we will delete it immediately.

For HealHub: When clinics record data for minor patients, the clinic is responsible for obtaining parental consent in accordance with applicable laws.`,
              },
              {
                title: '11. Grievance Officer',
                content: `As mandated by the DPDP Act 2023, we have designated a Grievance Officer:

Name: Ashish Uniyal
Email: a.uniyal@redcubicalsystems.com
Phone: +91 9368487416
Response Time: Within 30 days

For detailed grievance procedures, visit our Grievance Officer page.`,
              },
              {
                title: '12. Updates to This Compliance Statement',
                content: `We will update this compliance statement as:

— The DPDP Act 2023 and its rules are notified and implemented
— The Data Protection Board of India issues guidance
— Our products or data processing practices change

Last updated: February 2026`,
              },
            ].map((section, i) => (
              <div key={i} style={{
                paddingBottom: '40px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}>
                <h2 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '22px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '16px',
                }}>
                  {section.title}
                </h2>
                <div style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.55)',
                  lineHeight: '1.85',
                  whiteSpace: 'pre-line',
                }}>
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          {/* Related legal links */}
          <div style={{
            marginTop: '56px',
            padding: '32px',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '16px',
            marginBottom: '32px',
          }}>
            <p style={{
              fontSize: '12px', fontWeight: '700',
              color: 'rgba(255,255,255,0.3)',
              textTransform: 'uppercase', letterSpacing: '1px',
              marginBottom: '16px',
            }}>
              Related Legal Documents
            </p>
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '12px',
            }}>
              {[
                { label: '→ Privacy Policy', href: '/privacy-policy' },
                { label: '→ Terms of Service', href: '/terms-of-service' },
                { label: '→ Grievance Officer', href: '/grievance-officer' },
              ].map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  style={{
                    fontSize: '14px',
                    color: '#3B82F6',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.color = '#60A5FA')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.color = '#3B82F6')
                  }
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Back links */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Link to="/" className="btn-outline"
              style={{ padding: '12px 24px', fontSize: '13px' }}>
              ← Back to Home
            </Link>
            <Link to="/contact" className="btn-blue"
              style={{ padding: '12px 24px', fontSize: '13px' }}>
              Contact Us →
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 560px) {
          .dpdp-facts-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}